'use client';

/**
 * NavBreadcrumb - Icon Navigation + Breadcrumb Trail
 *
 * Unified breadcrumb system:
 * - Mobile icon buttons: Data-driven from NavigationProvider (database groups)
 * - Breadcrumb trail: Route registry for path patterns, params, parent chains
 * - Group metadata (labels, colors, order): From database via NavigationProvider
 *
 * Two-part navigation in header:
 * 1. Icon buttons for main sections (hover reveals full menu)
 * 2. Breadcrumb trail showing current location with sibling dropdowns
 *
 * Example: [🏠][📞][📁][🔍][🛡️] | Dashboard › Reports › Case #abc1...
 */

import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  ChevronRight,
  ChevronDown,
} from 'lucide-react';
import { useIsStaff, useIsAdmin, useCurrentRoute, useAccessControlState } from '../AppShellContext';
import { breadcrumbTokens, breadcrumbColorSchemes } from './breadcrumb';
import { routeRegistry } from '@/lib/routes/registry';
import { generateBreadcrumbs, substitutePathParams } from '@/lib/routes/breadcrumbs';
import { useNavigationOptional } from '@/lib/navigation/hooks';
import { resolveIcon } from '@/lib/navigation/icons';
import { getColorScheme as getNavColorScheme } from '@/lib/ui/colorSchemes';
import type { RouteConfig, BreadcrumbItem, RouteParams } from '@/lib/routes/types';
import type { ResolvedNavGroup, ResolvedNavItem, NavColorScheme } from '@/lib/navigation/types';
import type { LucideIcon } from 'lucide-react';
import { useMenuPopup } from '@/lib/a11y/useMenuPopup';

// ============================================================================
// Types
// ============================================================================

interface NavBreadcrumbProps {
  className?: string;
}

type BreadcrumbColor = 'blue' | 'amber' | 'emerald' | 'cyan' | 'purple' | 'slate';

type BreadcrumbIcon = LucideIcon | string;

interface BreadcrumbWithSiblings {
  id: string;
  label: string;
  href: string;
  current: boolean;
  icon?: BreadcrumbIcon;
  color: BreadcrumbColor;
  siblings: Array<{
    id: string;
    label: string;
    href: string;
    icon?: BreadcrumbIcon;
    active: boolean;
    color: BreadcrumbColor;
    group?: string;
    order?: number;
  }>;
}

// ============================================================================
// Color Schemes (using shared tokens)
// ============================================================================

const colorSchemes: Record<string, typeof breadcrumbColorSchemes.blue> = {
  ...breadcrumbColorSchemes,
  // Extend with slate for settings group
  slate: breadcrumbColorSchemes.purple, // Fallback to purple for breadcrumb display
};

// ============================================================================
// Helper: Map NavColorScheme to BreadcrumbColor
// ============================================================================

function toBreadcrumbColor(colorScheme: NavColorScheme | string | undefined): BreadcrumbColor {
  switch (colorScheme) {
    case 'blue': return 'blue';
    case 'amber': return 'amber';
    case 'emerald': return 'emerald';
    case 'cyan': return 'cyan';
    case 'purple': return 'purple';
    case 'slate': return 'purple'; // Settings uses purple in breadcrumbs
    default: return 'blue';
  }
}

// ============================================================================
// Helper: Get color from route (with DB navigation fallback)
// ============================================================================

function getRouteColor(
  route: RouteConfig,
  navGroups?: ResolvedNavGroup[]
): BreadcrumbColor {
  // Try to find color from database navigation groups
  if (navGroups && navGroups.length > 0) {
    // Check path against navigation items for an exact match
    for (const group of navGroups) {
      for (const item of group.items) {
        if (item.path === route.path || route.path.startsWith(item.path + '/')) {
          return toBreadcrumbColor(item.colorScheme || group.colorScheme);
        }
      }
    }
    // Check by group path patterns
    for (const group of navGroups) {
      for (const item of group.items) {
        if (route.path.startsWith(item.path.split('/').slice(0, 2).join('/'))) {
          return toBreadcrumbColor(group.colorScheme);
        }
      }
    }
  }

  // Fallback: derive from route config
  if (route.nav?.group) {
    switch (route.nav.group) {
      case 'navigation':
        if (route.path.startsWith('/adminpanel') || route.portal === 'admin') return 'purple';
        if (route.path.startsWith('/developer')) return 'cyan';
        if (route.path.includes('search')) return 'cyan';
        if (route.path.includes('report') || route.path.includes('recording')) return 'emerald';
        if (route.path.includes('call') || route.path.includes('dispatch')) return 'amber';
        return 'blue';
      case 'admin':
      case 'analytics':
      case 'settings':
        return 'purple';
      case 'developer':
        return 'cyan';
      default:
        return 'blue';
    }
  }

  if (route.portal === 'admin') return 'purple';
  if (route.portal === 'developer') return 'cyan';
  if (route.path.startsWith('/adminpanel')) return 'purple';

  return 'blue';
}

// ============================================================================
// Helper: Get siblings for a route (routes with same breadcrumbParent)
// ============================================================================

function getSiblings(
  route: RouteConfig,
  currentRouteId: string,
  params: Record<string, string> = {},
  navGroups?: ResolvedNavGroup[]
): BreadcrumbWithSiblings['siblings'] {
  if (!route.breadcrumbParent) {
    const mainNavRoutes = routeRegistry.getNavigableRoutes().filter(r =>
      r.nav &&
      !r.breadcrumbParent &&
      r.nav.positions?.includes('main') &&
      !r.nav.hidden
    );

    return mainNavRoutes.map(r => {
      const label = r.nav?.label ||
        (typeof r.breadcrumbLabel === 'string' ? r.breadcrumbLabel : null) ||
        (typeof r.title === 'string' ? r.title : r.id);

      return {
        id: r.id,
        label,
        href: r.path,
        icon: r.nav?.icon,
        active: r.id === currentRouteId,
        color: getRouteColor(r, navGroups),
        group: r.nav?.group,
        order: r.nav?.order ?? 999,
      };
    });
  }

  const allRoutes = routeRegistry.getAllRoutes();
  const siblings = allRoutes.filter(r => {
    if (r.breadcrumbParent !== route.breadcrumbParent) return false;
    if (r.hideBreadcrumb) return false;
    if (!r.nav && !r.breadcrumbLabel && r.id !== currentRouteId) return false;

    const hasDynamicSegment = r.path.includes('[');
    if (hasDynamicSegment) {
      const pathParams = r.path.match(/\[([^\]]+)\]/g);
      if (pathParams) {
        for (const paramMatch of pathParams) {
          const paramName = paramMatch.replace(/[\[\]\.]/g, '');
          if (!params[paramName]) {
            if (r.id !== currentRouteId) return false;
          }
        }
      }
    }

    return true;
  });

  return siblings.map(r => {
    const label = typeof r.breadcrumbLabel === 'string'
      ? r.breadcrumbLabel
      : r.nav?.label || (typeof r.title === 'string' ? r.title : r.id);

    let href = r.path;
    if (params && Object.keys(params).length > 0) {
      href = substitutePathParams(r.path, params);
    }

    return {
      id: r.id,
      label,
      href,
      icon: r.nav?.icon,
      active: r.id === currentRouteId,
      color: getRouteColor(r, navGroups),
      group: r.nav?.group,
      order: r.nav?.order ?? 999,
    };
  });
}

// ============================================================================
// Hook: Generate breadcrumbs from route registry
// ============================================================================

export function isAdminOnlyRoute(route: RouteConfig): boolean {
  return route.auth === 'admin' || route.auth === 'platform';
}

function maySeeBreadcrumbRoute(route: RouteConfig, isAdmin: boolean, isPlatformAdmin: boolean): boolean {
  if (!isAdminOnlyRoute(route)) return true;
  return route.auth === 'platform' ? isPlatformAdmin : isAdmin;
}

function useBreadcrumbsFromRegistry(
  pathname: string,
  navGroups?: ResolvedNavGroup[]
): BreadcrumbWithSiblings[] {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbWithSiblings[]>([]);
  const isAdmin = useIsAdmin();
  const isPlatformAdmin = useAccessControlState().userContext?.is_platform_admin === true;

  useEffect(() => {
    let isCancelled = false;

    async function buildBreadcrumbs() {
      const route = routeRegistry.getByPath(pathname);

      if (!route || route.hideBreadcrumb) {
        if (!isCancelled) {
          setBreadcrumbs([]);
        }
        return;
      }

      const params = routeRegistry.extractParams(pathname, route.path);

      const routeParams: RouteParams = {
        params,
        searchParams: {},
      };

      const chain = await generateBreadcrumbs(pathname, routeParams);

      if (isCancelled) return;

      if (chain.length === 0) {
        setBreadcrumbs([]);
        return;
      }

      const visibleChain = chain.filter((crumb) => {
        const crumbRoute = routeRegistry.getById(crumb.id);
        return crumb.current || !crumbRoute || maySeeBreadcrumbRoute(crumbRoute, isAdmin, isPlatformAdmin);
      });

      const withSiblings: BreadcrumbWithSiblings[] = visibleChain.map((crumb) => {
        const crumbRoute = routeRegistry.getById(crumb.id);
        const color = crumbRoute ? getRouteColor(crumbRoute, navGroups) : 'blue';
        const allSiblings = crumbRoute ? getSiblings(crumbRoute, crumb.id, params, navGroups) : [];
        const siblings = allSiblings.filter((sibling) => {
          const siblingRoute = routeRegistry.getById(sibling.id);
          return sibling.id === crumb.id || !siblingRoute || maySeeBreadcrumbRoute(siblingRoute, isAdmin, isPlatformAdmin);
        });

        return {
          id: crumb.id,
          label: crumb.label,
          href: crumb.href,
          current: crumb.current,
          icon: crumbRoute?.nav?.icon,
          color,
          siblings,
        };
      });

      setBreadcrumbs(withSiblings);
    }

    buildBreadcrumbs();

    return () => {
      isCancelled = true;
    };
  }, [pathname, navGroups, isAdmin, isPlatformAdmin]);

  return breadcrumbs;
}

// ============================================================================
// Hook: Build mobile navigation categories from DB groups
// ============================================================================

function useNavCategories() {
  const navContext = useNavigationOptional();
  const groups = navContext?.navigation?.byPosition.sidebar ?? [];

  return useMemo(() => {
    if (groups.length === 0) return [];

    // Filter to groups that should appear in mobile/main positions
    // IMPORTANT: resolve icons from iconName strings, NOT from g.icon —
    // g.icon may be a serialized object from localStorage cache (not a callable component)
    return groups
      .filter(g => g.visible && g.positions.some(p => p === 'mobile' || p === 'sidebar'))
      .map(g => ({
        key: g.key,
        label: g.label,
        href: g.items[0]?.path || '/',
        icon: resolveIcon(g.iconName),
        iconName: g.iconName,
        colorScheme: g.colorScheme,
        color: toBreadcrumbColor(g.colorScheme),
        items: g.items.filter(i => !i.hidden && i.positions.some(p => p === 'sidebar')),
        sortOrder: g.sortOrder,
      }))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [groups]);
}

// ============================================================================
// Hook: Build group label/order map from DB groups
// ============================================================================

function useGroupMetadata() {
  const navContext = useNavigationOptional();
  const groups = navContext?.navigation?.groups ?? [];

  return useMemo(() => {
    const labels: Record<string, string> = { default: '' };
    const order: Record<string, number> = { default: 99 };

    for (const group of groups) {
      labels[group.key] = group.label;
      order[group.key] = group.sortOrder;
    }

    // Also map route config group names to DB group data
    // Route config uses 'navigation' as a catch-all; map to operations
    labels['navigation'] = labels['operations'] || 'Navigation';
    order['navigation'] = order['operations'] ?? 1;

    return { labels, order };
  }, [groups]);
}

// ============================================================================
// Component
// ============================================================================

export function NavBreadcrumb({ className = '' }: NavBreadcrumbProps) {
  const pathname = usePathname();
  const route = useCurrentRoute();
  const isStaff = useIsStaff();

  // Data-driven navigation
  const navContext = useNavigationOptional();
  const navGroups = navContext?.navigation?.byPosition.sidebar;
  const categories = useNavCategories();
  const groupMeta = useGroupMetadata();

  // Helper to check if a category is active
  const isCategoryActive = (cat: { href: string; key: string }) => {
    if (pathname === cat.href || pathname.startsWith(cat.href + '/')) return true;
    return false;
  };

  // Generate breadcrumbs from route registry (single source of truth for trail)
  const breadcrumbsWithSiblings = useBreadcrumbsFromRegistry(pathname, navGroups);

  // Find current category for color
  const currentCategory = categories.find(cat => isCategoryActive(cat));
  const currentColor = currentCategory?.color || 'blue';

  if (!isStaff) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 min-w-0 ${className}`}>
      {/* NOTE: Hardcoded Home icon removed — sidebar/header navigation is fully
          data-driven from navigation_groups in the database. If a "home" group
          is desired, add it to the DB (see migration 20260328_nav_restructure). */}

      {/* Mobile: Icon Navigation — data-driven from DB groups */}
      <div className="flex md:hidden items-center gap-0.5">
        {categories.map((category) => (
          <NavIconButton
            key={category.key}
            category={category}
            isActive={isCategoryActive(category)}
            currentPath={pathname}
          />
        ))}
      </div>

      {/* Desktop: Breadcrumb Trail with Dropdown Menus */}
      {breadcrumbsWithSiblings.length > 0 && (
        <nav className="hidden md:flex items-center gap-1 min-w-0 flex-1" aria-label="Breadcrumb">
          {breadcrumbsWithSiblings.map((crumb, index) => (
            <BreadcrumbDropdown
              key={crumb.id}
              crumb={crumb}
              isLast={index === breadcrumbsWithSiblings.length - 1}
              color={crumb.color || currentColor}
              groupMeta={groupMeta}
            />
          ))}
        </nav>
      )}
    </div>
  );
}

// ============================================================================
// Hook: Track an element's viewport position so a portaled dropdown can follow
// it without being clipped by parent stacking contexts / overflow.
// ============================================================================

function useAnchorPosition(
  anchorRef: React.RefObject<HTMLElement | null>,
  isOpen: boolean
): { top: number; left: number } | null {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen || !anchorRef.current) {
      setPos(null);
      return;
    }

    const update = () => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({ top: rect.bottom, left: rect.left });
    };

    update();

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, anchorRef]);

  return pos;
}

// ============================================================================
// BreadcrumbDropdown Component - Desktop breadcrumb with sibling dropdown
// ============================================================================

interface BreadcrumbDropdownProps {
  crumb: BreadcrumbWithSiblings;
  isLast: boolean;
  color: BreadcrumbColor;
  groupMeta: { labels: Record<string, string>; order: Record<string, number> };
}

function BreadcrumbDropdown({ crumb, isLast, color, groupMeta }: BreadcrumbDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const crumbRef = useRef<HTMLElement>(null);
  const colors = colorSchemes[color] || colorSchemes.blue;
  const hasSiblings = crumb.siblings.length > 1;
  const anchorPos = useAnchorPosition(triggerRef, isOpen && hasSiblings);
  // WCAG 2.1.1 / 2.4.3: Escape closes the sibling menu; focus goes back to the crumb when it closes from inside the menu.
  useMenuPopup({ open: isOpen && hasSiblings, onClose: () => setIsOpen(false), triggerRef: crumbRef, popupRef: menuRef });

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handleDropdownItemClick = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Group siblings by nav.group and sort using DB group metadata
  const groupedSiblings = useMemo(() => {
    const groups = new Map<string, typeof crumb.siblings>();
    for (const sibling of crumb.siblings) {
      const groupKey = sibling.group || 'default';
      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(sibling);
    }

    for (const items of groups.values()) {
      items.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }

    // Sort groups using DB group order
    const sortedGroups = Array.from(groups.entries()).sort(([a], [b]) => {
      const orderA = groupMeta.order[a] ?? 99;
      const orderB = groupMeta.order[b] ?? 99;
      return orderA - orderB;
    });

    return sortedGroups;
  }, [crumb.siblings, groupMeta.order]);

  const hasMultipleGroups = groupedSiblings.length > 1;

  return (
    <div className="flex items-center gap-1 min-w-0">
      <div
        ref={dropdownRef}
        className="relative min-w-0"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={triggerRef} className="min-w-0">
        {/* Breadcrumb Text/Link
            The label span uses `truncate` so very long dynamic crumbs (case
            numbers, file names) get an ellipsis instead of overflowing. We
            cap each crumb at ~10rem on small screens / ~14rem from md up so
            short labels like "Training" or "Achievements" never get cut to
            "Tr..." while there is still horizontal room available. */}
        {isLast ? (
          <span
            ref={crumbRef}
            className={`text-sm font-medium ${colors.text} flex items-center gap-1.5 px-2 py-1 rounded-md ${colors.bg} cursor-pointer min-w-0`}
            onClick={() => hasSiblings && setIsOpen(!isOpen)}
            // WCAG 2.1.1 / 4.1.2: the sibling menu also opens from the keyboard.
            role={hasSiblings ? 'button' : undefined}
            tabIndex={hasSiblings ? 0 : undefined}
            aria-haspopup={hasSiblings ? 'menu' : undefined}
            aria-expanded={hasSiblings ? isOpen : undefined}
            onKeyDown={(e) => {
              if (hasSiblings && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setIsOpen(!isOpen);
              }
            }}
          >
            {crumb.icon && typeof crumb.icon !== 'string' && <crumb.icon className="w-3.5 h-3.5 flex-shrink-0" />}
            <span className="truncate max-w-[10rem] md:max-w-[14rem]">{crumb.label}</span>
            {hasSiblings && <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
          </span>
        ) : (
          <Link
            ref={crumbRef as React.Ref<HTMLAnchorElement>}
            href={crumb.href}
            className={`text-sm text-white/60 hover:text-white flex items-center gap-1.5 px-2 py-1 rounded-md transition-all min-w-0
              ${isOpen ? 'bg-white/[0.06]' : 'hover:bg-white/[0.04]'}
            `}
          >
            {crumb.icon && typeof crumb.icon !== 'string' && <crumb.icon className="w-3.5 h-3.5 flex-shrink-0" />}
            <span className="truncate max-w-[10rem] md:max-w-[14rem]">{crumb.label}</span>
            {hasSiblings && <ChevronDown className={`w-3 h-3 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
          </Link>
        )}

        </div>

        {/* Siblings Dropdown - Portaled to body to escape header stacking context */}
        {isOpen && hasSiblings && anchorPos && typeof document !== 'undefined' && createPortal(
          <div
            ref={menuRef}
            className="fixed z-[1000]"
            style={{ top: anchorPos.top, left: anchorPos.left }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="h-2" />
            <div className={`w-56 py-1.5 bg-[#0a0a0a] border ${colors.border} rounded-lg shadow-2xl max-h-[70vh] overflow-y-auto`}>
              {groupedSiblings.map(([groupKey, siblings], groupIndex) => (
                <div key={groupKey}>
                  {/* Group header — labels from DB */}
                  {hasMultipleGroups && groupMeta.labels[groupKey] && (
                    <div className={`px-3 py-1.5 ${groupIndex > 0 ? 'mt-1.5 border-t border-white/[0.06]' : ''}`}>
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                        {groupMeta.labels[groupKey]}
                      </span>
                    </div>
                  )}
                  {hasMultipleGroups && !groupMeta.labels[groupKey] && groupIndex > 0 && (
                    <div className="my-1.5 border-t border-white/[0.06]" />
                  )}
                  {siblings.map((sibling) => {
                    const SiblingIcon = typeof sibling.icon !== 'string' ? sibling.icon : undefined;
                    const siblingColors = colorSchemes[sibling.color || color] || colorSchemes.blue;
                    return (
                      <Link
                        key={sibling.id}
                        href={sibling.href}
                        onClick={handleDropdownItemClick}
                        aria-current={sibling.active ? 'page' : undefined}
                        className={`
                          flex items-center gap-3 px-3 py-2 mx-1.5 rounded-lg transition-all duration-200 group
                          ${sibling.active ? siblingColors.bg : 'hover:bg-white/[0.04]'}
                        `}
                      >
                        {SiblingIcon && (
                          <div className={`p-1 rounded ${siblingColors.bg} group-hover:scale-105 transition-all duration-200`}>
                            <SiblingIcon className={`w-3.5 h-3.5 ${siblingColors.text}`} />
                          </div>
                        )}
                        <span className={`text-sm ${sibling.active ? siblingColors.text : 'text-white/70 group-hover:text-white'} transition-colors`}>
                          {sibling.label}
                        </span>
                        {sibling.active && (
                          <div className={`ml-auto w-1.5 h-1.5 rounded-full ${siblingColors.text.replace('text-', 'bg-')}`} />
                        )}
                      </Link>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Chevron separator */}
      {!isLast && <ChevronRight className={`${breadcrumbTokens.separator.icon} ${breadcrumbTokens.separator.base} flex-shrink-0`} />}
    </div>
  );
}

// ============================================================================
// NavIconButton - Icon button with hover dropdown (mobile)
// Data-driven from DB navigation groups
// ============================================================================

interface NavCategoryData {
  key: string;
  label: string;
  href: string;
  icon: LucideIcon | null;
  iconName: string | null;
  colorScheme: NavColorScheme;
  color: BreadcrumbColor;
  items: ResolvedNavItem[];
  sortOrder: number;
}

interface NavIconButtonProps {
  category: NavCategoryData;
  isActive: boolean;
  currentPath: string;
}

function NavIconButton({ category, isActive, currentPath }: NavIconButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLAnchorElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const colors = colorSchemes[category.color] || colorSchemes.blue;
  // Resolve icon from string name at render time (cached objects from localStorage aren't components)
  const Icon = resolveIcon(category.iconName);
  // Limit to 6 items for dropdown
  const categoryItems = category.items.slice(0, 6);
  const anchorPos = useAnchorPosition(triggerRef, isOpen && categoryItems.length > 0);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handleDropdownItemClick = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inTrigger = dropdownRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={dropdownRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Icon Button */}
      <Link
        ref={triggerRef}
        href={category.href}
        aria-current={isActive ? 'true' : undefined}
        className={`
          flex items-center justify-center p-2 rounded-lg transition-all duration-200 group
          ${isActive ? colors.bg : 'hover:bg-white/[0.06]'}
        `}
        title={category.label}
      >
        <div className={`
          p-1.5 rounded-md transition-all duration-200
          ${isActive ? colors.bg : 'bg-white/[0.04]'}
          ${isOpen ? `${colors.bg} scale-110` : ''}
        `}>
          {Icon && <Icon className={`w-4 h-4 ${isActive || isOpen ? colors.text : 'text-white/60'}`} />}
        </div>
      </Link>

      {/* Dropdown Menu - Portaled to body to escape header stacking context */}
      {isOpen && categoryItems.length > 0 && anchorPos && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed z-[1000]"
          style={{ top: anchorPos.top, left: anchorPos.left }}
          ref={menuRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="h-2" />

          <div className={`w-64 py-1.5 bg-[#0a0a0a] border ${colors.border} rounded-lg shadow-2xl`}>
            {/* Category Header */}
            <div className="px-4 py-2 border-b border-white/[0.06] mb-1">
              <div className="flex items-center gap-2">
                <div className={`w-1.5 h-1.5 rounded-full ${colors.text.replace('text-', 'bg-')}`} />
                <span className={`text-xs font-semibold uppercase tracking-wider ${colors.text}`}>
                  {category.label}
                </span>
              </div>
            </div>

            {/* Menu Items — from DB navigation items */}
            {categoryItems.map((item) => {
              const ItemIcon = resolveIcon(item.iconName);
              const itemPath = item.path.split('?')[0];
              const isItemActive = currentPath === itemPath || currentPath.startsWith(itemPath + '/');

              return (
                <Link
                  key={item.key}
                  href={item.path}
                  onClick={handleDropdownItemClick}
                  aria-current={isItemActive ? 'page' : undefined}
                  className={`
                    flex items-start gap-3 px-3 py-2.5 mx-1.5 rounded-lg transition-all duration-200 group
                    ${isItemActive ? colors.bg : 'hover:bg-white/[0.04]'}
                  `}
                >
                  {ItemIcon && (
                    <div className={`p-1.5 rounded-md ${colors.bg} group-hover:scale-105 transition-all duration-200`}>
                      <ItemIcon className={`w-4 h-4 ${colors.text}`} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isItemActive ? colors.text : 'text-white/80 group-hover:text-white'} transition-colors`}>
                      {item.label}
                    </p>
                    {item.description && (
                      <p className="text-xs text-white/70 mt-0.5 truncate">
                        {item.description}
                      </p>
                    )}
                  </div>
                  {isItemActive && (
                    <div className={`mt-1.5 w-1.5 h-1.5 rounded-full ${colors.text.replace('text-', 'bg-')}`} />
                  )}
                </Link>
              );
            })}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// ============================================================================
// Export
// ============================================================================

export default NavBreadcrumb;
